/** The adventure map: dense per-cell arrays plus the setters that keep the
 * derived `moveCost` plane in sync (Section 4, state.ts `AdventureMapState`).
 *
 * Numbers come from the wrenchhands reference, "# 7. Adventure-Map Movement" and
 * "# 8. Terrain". One plain land step is `MOVE_COST_BASE` = 100 movement points,
 * which is why hero day pools are quoted as 1500-2000: that is 15-20 plain tiles.
 */

import type { ObjectId } from "../ids.js";
import { asModifierSrcId } from "../ids.js";
import type { Explained, Modifier } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { mulFp } from "../numeric.js";
import type { AdventureMapState, RiverKind, RoadKind, TerrainKind } from "../state.js";
import { ROAD_KINDS, RIVER_KINDS, TERRAINS, cellIndex, inBounds } from "../state.js";

// ---------------------------------------------------------------- constants

/** One plain (grass/dirt/lava/cavern) land step. Everything else is relative to it. */
export const MOVE_COST_BASE = 100;

/** `moveCost === 0` is the single encoding of "a hero may not enter this cell". */
export const IMPASSABLE = 0;

/**
 * Base step cost by terrain, in movement points, indexed to match `TERRAINS`.
 *
 * Grass, dirt, lava and cavern (subterranean) are the 100-point terrains; sand
 * and snow are 150; swamp is the worst walkable at 175; rough, highlands and
 * wasteland are 125. Water is 100 but only for a hero in a boat - passability is decided by `MoveMode`, not
 * by the cost. Rock is the map's structural filler and is never enterable.
 */
export const TERRAIN_COST: Readonly<Record<TerrainKind, number>> = Object.freeze({
  water: 100,
  sand: 150,
  dirt: 100,
  grass: 100,
  snow: 150,
  swamp: 175,
  rough: 125,
  cavern: 100,
  lava: 100,
  rock: IMPASSABLE,
  highlands: 125,
  wasteland: 125,
});

/** Terrain that a land hero can never walk on regardless of cost. */
export const TERRAIN_WATER: Readonly<Record<TerrainKind, boolean>> = Object.freeze({
  water: true, sand: false, dirt: false, grass: false, snow: false,
  swamp: false, rough: false, cavern: false, lava: false, rock: false,
  highlands: false, wasteland: false,
});

/**
 * Road step cost. A road replaces the terrain cost outright rather than
 * discounting it, so a cobblestone road across swamp costs 50, not 175 * x.
 */
export const ROAD_COST: Readonly<Record<RoadKind, number>> = Object.freeze({
  none: 0, dirt: 75, gravel: 65, cobble: 50,
});

/**
 * The expansion Pathfinding (reference "# 5", the expansion table): the step cost drops by
 * 25 / 50 / 75 points but never below 95 / 90 / 85. The floors are what make it
 * a general movement skill rather than a rough-terrain skill - on grass, expert
 * Pathfinding still buys 100 -> 85. Index 0 is "no Pathfinding".
 */
export const PATHFINDING_REDUCTION: readonly number[] = Object.freeze([0, 25, 50, 75]);
export const PATHFINDING_FLOOR: readonly number[] = Object.freeze([0, 95, 90, 85]);

/**
 * Diagonal steps cost exactly what orthogonal steps cost.
 *
 * This is a genuine the classic quirk and it is load-bearing for pathing: a diagonal
 * covers sqrt(2) tiles of ground for one tile of price, so optimal routes are
 * diagonal-first. The wrenchhands reference describes the alternative reading
 * ("diagonal cost is approximately straight cost x 1.41"); that variant is
 * reachable by setting `diagonalFp` to `DIAGONAL_FP_SCALED` and is not the
 * default, because "fixing" it changes every reachable set in the game.
 */
export const DIAGONAL_FP_EQUAL = 1000;
export const DIAGONAL_FP_SCALED = 1414;

/** Neighbour offsets, clockwise from north, matching `HeroState.facing` 0..7. */
export const NEIGHBOUR_DX: readonly number[] = Object.freeze([0, 1, 1, 1, 0, -1, -1, -1]);
export const NEIGHBOUR_DY: readonly number[] = Object.freeze([-1, -1, 0, 1, 1, 1, 0, -1]);
/** True where the corresponding offset is a diagonal. */
export const NEIGHBOUR_DIAGONAL: readonly boolean[] =
  Object.freeze([false, true, false, true, false, true, false, true]);

const TERRAIN_INDEX_COST = Int32Array.from(TERRAINS.map((t) => TERRAIN_COST[t]));
const TERRAIN_INDEX_WATER = Uint8Array.from(TERRAINS.map((t) => (TERRAIN_WATER[t] ? 1 : 0)));
const ROAD_INDEX_COST = Int32Array.from(ROAD_KINDS.map((r) => ROAD_COST[r]));

// ---------------------------------------------------------------- creation

export interface CreateMapOptions {
  /** Terrain every cell starts as. Defaults to grass, the cheapest neutral land. */
  readonly terrain?: TerrainKind;
}

/**
 * Allocate a `width * height * levels` map. Every plane is filled with one
 * terrain, no roads, no rivers, no occupants, and `moveCost` already derived -
 * the arrays are never left in a state where `moveCost` disagrees with terrain.
 */
export function createMap(
  width: number, height: number, levels: number, opts: CreateMapOptions = {},
): AdventureMapState {
  const w = Math.trunc(width), h = Math.trunc(height), l = Math.trunc(levels);
  if (w <= 0 || h <= 0 || l <= 0) throw new Error(`bad map size ${w}x${h}x${l}`);
  const n = w * h * l;

  const fill = opts.terrain ?? "grass";
  const ti = TERRAINS.indexOf(fill);
  if (ti < 0) throw new Error(`unknown terrain "${fill}"`);

  const terrain = new Uint8Array(n).fill(ti);
  const moveCost = new Uint16Array(n).fill(TERRAIN_INDEX_COST[ti]!);

  return {
    width: w,
    height: h,
    levels: l,
    terrain,
    variant: new Uint8Array(n),
    road: new Uint8Array(n),
    river: new Uint8Array(n),
    // Ordinary ground everywhere, and a legend whose slot 0 says so; see
    // `AdventureMapState.ground`.
    ground: new Uint8Array(n),
    groundIndex: [null],
    moveCost,
    occupant: new Uint16Array(n),
    // Slot 0 is reserved so that `occupant === 0` can mean "no object".
    objectIndex: [null],
  };
}

// ---------------------------------------------------------------- accessors

export const terrainAt = (m: AdventureMapState, i: number): TerrainKind =>
  TERRAINS[m.terrain[i]!]!;

export const roadAt = (m: AdventureMapState, i: number): RoadKind =>
  ROAD_KINDS[m.road[i]!]!;

export const riverAt = (m: AdventureMapState, i: number): RiverKind =>
  RIVER_KINDS[m.river[i]!]!;

/** Intrinsic cost of standing on a cell: the road cost if roaded, else terrain. */
export const tileCost = (m: AdventureMapState, i: number): number => m.moveCost[i]!;

export const isWaterCell = (m: AdventureMapState, i: number): boolean =>
  TERRAIN_INDEX_WATER[m.terrain[i]!] === 1;

export const isPassable = (m: AdventureMapState, i: number): boolean =>
  m.moveCost[i] !== IMPASSABLE;

export const occupantAt = (m: AdventureMapState, i: number): ObjectId | null =>
  m.objectIndex[m.occupant[i]!] ?? null;

export const cellCount = (m: AdventureMapState): number => m.width * m.height * m.levels;

export function indexOf(m: AdventureMapState, x: number, y: number, z: number): number {
  if (!inBounds(m, x, y, z)) return -1;
  return cellIndex(m, x, y, z);
}

/** Decompose a flat index. The inverse of `cellIndex`. */
export function cellCoords(m: AdventureMapState, i: number): [number, number, number] {
  const plane = m.width * m.height;
  const z = Math.trunc(i / plane);
  const r = i - z * plane;
  return [r % m.width, Math.trunc(r / m.width), z];
}

// ------------------------------------------------------------------ setters

/** The map arrays are mutable in place during construction and object edits. */
type MutableMap = {
  -readonly [K in keyof AdventureMapState]: AdventureMapState[K] extends readonly (infer E)[]
    ? E[] : AdventureMapState[K];
};

/**
 * Object blocking has no field of its own in `AdventureMapState`, so it is
 * encoded as "there is an occupant here and the derived cost is 0". That keeps
 * a terrain edit under a blocking object from silently re-opening the cell.
 */
const blockedByObject = (m: AdventureMapState, i: number): boolean =>
  m.occupant[i] !== 0 && m.moveCost[i] === IMPASSABLE && TERRAIN_INDEX_COST[m.terrain[i]!] !== 0;

function derivedCost(m: AdventureMapState, i: number): number {
  const t = TERRAIN_INDEX_COST[m.terrain[i]!]!;
  if (t === IMPASSABLE) return IMPASSABLE;
  const r = ROAD_INDEX_COST[m.road[i]!]!;
  return r === 0 ? t : r;
}

/** Recompute one cell's `moveCost` from terrain, road and object blocking. */
export function refreshMoveCost(m: AdventureMapState, i: number): void {
  const mm = m as MutableMap;
  mm.moveCost[i] = blockedByObject(m, i) ? IMPASSABLE : derivedCost(m, i);
}

export function setTerrain(
  m: AdventureMapState, x: number, y: number, z: number, terrain: TerrainKind,
): void {
  const i = indexOf(m, x, y, z);
  if (i < 0) return;
  const ti = TERRAINS.indexOf(terrain);
  if (ti < 0) return;
  const blocked = blockedByObject(m, i);
  (m as MutableMap).terrain[i] = ti;
  (m as MutableMap).moveCost[i] = blocked ? IMPASSABLE : derivedCost(m, i);
}

export function setRoad(
  m: AdventureMapState, x: number, y: number, z: number, road: RoadKind,
): void {
  const i = indexOf(m, x, y, z);
  if (i < 0) return;
  const ri = ROAD_KINDS.indexOf(road);
  if (ri < 0) return;
  const blocked = blockedByObject(m, i);
  (m as MutableMap).road[i] = ri;
  (m as MutableMap).moveCost[i] = blocked ? IMPASSABLE : derivedCost(m, i);
}

/** Rivers are decoration for movement; they matter to map generation and art. */
export function setRiver(
  m: AdventureMapState, x: number, y: number, z: number, river: RiverKind,
): void {
  const i = indexOf(m, x, y, z);
  if (i < 0) return;
  const ri = RIVER_KINDS.indexOf(river);
  if (ri >= 0) (m as MutableMap).river[i] = ri;
}

export function setVariant(
  m: AdventureMapState, x: number, y: number, z: number, variant: number,
): void {
  const i = indexOf(m, x, y, z);
  if (i >= 0) (m as MutableMap).variant[i] = variant & 0xff;
}

/** Reserve (or find) the dense slot for an object id. Slot 0 is always `null`. */
/**
 * Reverse of `objectIndex`, cached OUTSIDE the state.
 *
 * `internObject` used to answer by scanning the table, which is linear in the
 * number of objects already on the map and is called once per object per cell
 * it occupies - quadratic overall. On a 252x252 map the generator stamps some
 * five thousand objects and that scan was the single most expensive thing in
 * generation: twenty of its forty-five seconds, most of it in the ridge
 * dressing, comparing branded strings.
 *
 * The cache lives in a `WeakMap` keyed by the map and not in `AdventureMapState`
 * because the state is persisted and hashed, and a derived index has no business
 * in either. `objectIndex` is append-only - nothing in the kernel ever removes
 * from it, `setOccupant(null)` only clears the cell's slot - so the cache can be
 * kept in step by writing to both on the one path that grows it. The size check
 * rebuilds it for a map that arrived from disk, or from anywhere else that did
 * not come through here.
 */
const REVERSE_INDEX = new WeakMap<AdventureMapState, Map<ObjectId, number>>();

function reverseIndex(m: AdventureMapState): Map<ObjectId, number> {
  const list = (m as MutableMap).objectIndex;
  let index = REVERSE_INDEX.get(m);
  if (index === undefined || index.size !== list.length - 1) {
    index = new Map<ObjectId, number>();
    for (let i = 1; i < list.length; i++) {
      const id = list[i];
      if (id !== null && id !== undefined) index.set(id, i);
    }
    REVERSE_INDEX.set(m, index);
  }
  return index;
}

export function internObject(m: AdventureMapState, id: ObjectId): number {
  const list = (m as MutableMap).objectIndex;
  const index = reverseIndex(m);
  const found = index.get(id);
  if (found !== undefined) return found;
  if (list.length > 0xffff) throw new Error("map object table overflow");
  list.push(id);
  index.set(id, list.length - 1);
  return list.length - 1;
}

/**
 * Place or clear the object footprint marker on a cell. `blocks` writes the
 * impassable encoding; an entrance cell of the same object is placed with
 * `blocks: false` so a hero can step onto it and trigger the visit.
 */
export function setOccupant(
  m: AdventureMapState, x: number, y: number, z: number,
  id: ObjectId | null, blocks = true,
): void {
  const i = indexOf(m, x, y, z);
  if (i < 0) return;
  const mm = m as MutableMap;
  if (id === null) {
    mm.occupant[i] = 0;
    mm.moveCost[i] = derivedCost(m, i);
    return;
  }
  mm.occupant[i] = internObject(m, id);
  mm.moveCost[i] = blocks ? IMPASSABLE : derivedCost(m, i);
}

// ------------------------------------------------------------- move rules

export type MoveMode = "land" | "sea" | "fly" | "waterWalk";

/**
 * Everything about the moving hero that changes a step's price. Built once per
 * hero per turn by the caller and handed to the pathfinder; nothing in here is
 * read out of `GameState`, so a reachable set is a pure function of
 * (map, origin, rules, budget).
 */
export interface MoveRules {
  readonly mode: MoveMode;
  /** 0 none, 1 basic, 2 advanced, 3 expert. */
  readonly pathfinding: 0 | 1 | 2 | 3;
  /** Terrain the whole army is native to, or null. */
  readonly nativeTerrain: TerrainKind | null;
  /** Fixed point at FP_ONE. `DIAGONAL_FP_EQUAL` is the classic default. */
  readonly diagonalFp: number;
  /** Movement points available; `computeReachable` uses it for `reachable`. */
  readonly budget: number;
  /**
   * The classic lets a hero take one more step as long as any movement point remains,
   * however expensive that step is. Turning it off gives strict budgeting.
   */
  readonly lastStepFree: boolean;
  /**
   * Land-mode heroes who may step onto water cells (Blackwater, army-pure).
   * Mixed armies still need a boat; this never opens rock.
   */
  readonly amphibious: boolean;
  /**
   * River overlay cells cost as a dirt road when both ends of the step are
   * rivered. A real road still wins if it is cheaper.
   */
  readonly riverAsRoad: boolean;
  /**
   * Bit mask of `RIVER_KINDS` indices that cost as `riverRoadCost` when both
   * ends of the step carry that overlay. `riverAsRoad` still means every kind.
   */
  readonly riverRoadMask: number;
  readonly riverRoadCost: number;
  /** Cap a road step at this cost; 0 means no cap. */
  readonly roadCostCap: number;
  /** Extra terrains that cap at a plain grass step, on top of `nativeTerrain`. */
  readonly extraNative: readonly TerrainKind[];
  /** Absolute off-road costs that replace the terrain table. */
  readonly terrainCosts: Readonly<Partial<Record<TerrainKind, number>>>;
  /** Underground (z > 0) walkable terrain caps at a plain grass step. */
  readonly underworldNative: boolean;
  /** May enter a water cell that has a land neighbour. */
  readonly shoreWalk: boolean;
  /**
   * Cells enterable whatever the mode says: the hull a hero on foot may board,
   * the shore a hero aboard may land on. Both are "the one step that changes
   * the mode", so they are handed in by whoever knows where the boats are
   * rather than derived from terrain here. Null when there are none.
   */
  readonly extraCells: ReadonlySet<number> | null;
}

const NO_NATIVE: readonly TerrainKind[] = Object.freeze([]);
const NO_TERRAIN_COSTS: Readonly<Partial<Record<TerrainKind, number>>> = Object.freeze({});

export const DEFAULT_MOVE_RULES: MoveRules = Object.freeze({
  mode: "land" as MoveMode,
  pathfinding: 0 as const,
  nativeTerrain: null,
  diagonalFp: DIAGONAL_FP_EQUAL,
  budget: 1500,
  lastStepFree: true,
  amphibious: false,
  riverAsRoad: false,
  riverRoadMask: 0,
  riverRoadCost: ROAD_COST.dirt,
  roadCostCap: 0,
  extraNative: NO_NATIVE,
  terrainCosts: NO_TERRAIN_COSTS,
  underworldNative: false,
  shoreWalk: false,
  extraCells: null,
});

export function moveRules(partial: Partial<MoveRules> = {}): MoveRules {
  return { ...DEFAULT_MOVE_RULES, ...partial };
}

/** Bit mask of `RIVER_KINDS` indices for `MoveRules.riverRoadMask`. */
export function riverKindMask(kinds: readonly string[] | undefined): number {
  if (kinds === undefined || kinds.length === 0) return 0;
  let mask = 0;
  for (const k of kinds) {
    const i = RIVER_KINDS.indexOf(k as RiverKind);
    if (i > 0) mask |= 1 << i;
  }
  return mask;
}

/** Can a hero under these rules occupy this cell at all? */
export function cellEnterable(m: AdventureMapState, i: number, rules: MoveRules): boolean {
  if (rules.extraCells !== null && rules.extraCells.has(i)) return m.moveCost[i] !== IMPASSABLE;
  const water = isWaterCell(m, i);
  switch (rules.mode) {
    case "sea": return water && m.moveCost[i] !== IMPASSABLE;
    // Flight crosses water and object footprints but not structural rock.
    case "fly": return TERRAIN_INDEX_COST[m.terrain[i]!] !== IMPASSABLE;
    case "waterWalk": return m.moveCost[i] !== IMPASSABLE;
    case "land":
      if (water) {
        if (m.moveCost[i] === IMPASSABLE) return false;
        if (rules.amphibious) return true;
        return rules.shoreWalk && hasLandNeighbour(m, i);
      }
      return m.moveCost[i] !== IMPASSABLE;
  }
}

function hasLandNeighbour(m: AdventureMapState, i: number): boolean {
  const [x, y, z] = cellCoords(m, i);
  for (let k = 0; k < 8; k++) {
    const nx = x + NEIGHBOUR_DX[k]!, ny = y + NEIGHBOUR_DY[k]!;
    if (!inBounds(m, nx, ny, z)) continue;
    const j = cellIndex(m, nx, ny, z);
    if (!isWaterCell(m, j) && TERRAIN_INDEX_COST[m.terrain[j]!] !== IMPASSABLE) return true;
  }
  return false;
}

// ------------------------------------------------------------ step costing

const SRC_TERRAIN = TERRAINS.map((t) => asModifierSrcId(`terrain.${t}`));
const SRC_ROAD = ROAD_KINDS.map((r) => asModifierSrcId(`road.${r}`));
const SRC_PATHFINDING = asModifierSrcId("skill.pathfinding");
const SRC_PATHFINDING_FLOOR = asModifierSrcId("skill.pathfinding.floor");
const SRC_NATIVE = asModifierSrcId("terrain.native");
const SRC_RIVER_ROAD = asModifierSrcId("river.road");
const SRC_DIAGONAL = asModifierSrcId("geometry.diagonal");
const SRC_ROAD_CAP = asModifierSrcId("road.cap");
const SRC_TERRAIN_COST = asModifierSrcId("terrain.cost");

function roadPairCost(roadFrom: number, roadTo: number, rules: MoveRules): number {
  const raw = Math.max(ROAD_INDEX_COST[roadFrom]!, ROAD_INDEX_COST[roadTo]!);
  if (rules.roadCostCap > 0 && raw > rules.roadCostCap) return rules.roadCostCap;
  return raw;
}

function riverPairCost(m: AdventureMapState, from: number, to: number, rules: MoveRules): number | null {
  const rf = m.river[from]!, rt = m.river[to]!;
  if (rf === 0 || rt === 0) return null;
  if (rules.riverAsRoad) return rules.riverRoadCost;
  const mask = rules.riverRoadMask;
  if (mask !== 0 && (mask & (1 << rf)) !== 0 && (mask & (1 << rt)) !== 0) return rules.riverRoadCost;
  return null;
}

function isNativeTerrain(rules: MoveRules, terrainIdx: number, z: number): boolean {
  const t = TERRAINS[terrainIdx]!;
  if (rules.nativeTerrain === t) return true;
  for (const x of rules.extraNative) if (x === t) return true;
  if (rules.underworldNative && z > 0) return true;
  return false;
}

function offRoadTerrainCost(
  m: AdventureMapState, to: number, rules: MoveRules,
): { readonly cost: number; readonly overridden: boolean } {
  const terrainIdx = m.terrain[to]!;
  const t = TERRAINS[terrainIdx]!;
  const override = rules.terrainCosts[t];
  if (override !== undefined) return { cost: override, overridden: true };
  let cost = TERRAIN_INDEX_COST[terrainIdx]!;
  if (rules.pathfinding > 0) {
    cost -= PATHFINDING_REDUCTION[rules.pathfinding]!;
    const floor = PATHFINDING_FLOOR[rules.pathfinding]!;
    if (cost < floor) cost = floor;
  }
  const [, , z] = cellCoords(m, to);
  if (isNativeTerrain(rules, terrainIdx, z) && cost > MOVE_COST_BASE) cost = MOVE_COST_BASE;
  return { cost, overridden: false };
}

/** Shared stack group: a road and the terrain under it are mutually exclusive. */
const GROUP_SURFACE = "movement.surface";

/**
 * The modifiers that price one step from `from` to `to`.
 *
 * The road discount is a classic pairwise rule: it applies only when *both* cells
 * carry a road, and the worse (more expensive) of the two roads governs, so a
 * cobblestone tile next to a dirt tile is a 75-point step. Off-road, Pathfinding
 * applies; on a road it does not, because its floor (85 at expert) is worse than
 * every road and it would otherwise make roads slower.
 */
export function stepModifiers(
  m: AdventureMapState, from: number, to: number, rules: MoveRules, diagonal: boolean,
): readonly Modifier[] {
  const mods: Modifier[] = [];

  const roadFrom = m.road[from]!, roadTo = m.road[to]!;
  const onRoad = roadFrom !== 0 && roadTo !== 0;
  const riverCost = onRoad ? null : riverPairCost(m, from, to, rules);
  const terrainIdx = m.terrain[to]!;

  if (onRoad) {
    // The worse road of the pair; roads are ordered none < dirt < gravel < cobble
    // by descending cost, so "worse" is the larger cost.
    const raw = Math.max(ROAD_INDEX_COST[roadFrom]!, ROAD_INDEX_COST[roadTo]!);
    const cost = roadPairCost(roadFrom, roadTo, rules);
    const worse = ROAD_INDEX_COST[roadFrom]! >= ROAD_INDEX_COST[roadTo]! ? roadFrom : roadTo;
    mods.push({
      source: SRC_ROAD[worse]!, stat: "movement", op: "add",
      value: raw - MOVE_COST_BASE, stackGroup: GROUP_SURFACE, stackRule: "first",
    });
    if (cost < raw) {
      mods.push({ source: SRC_ROAD_CAP, stat: "movement", op: "min", value: cost });
    }
  } else if (riverCost !== null) {
    mods.push({
      source: SRC_RIVER_ROAD, stat: "movement", op: "add",
      value: riverCost - MOVE_COST_BASE, stackGroup: GROUP_SURFACE, stackRule: "first",
    });
  }

  // Always emitted, so a tooltip can show the terrain a road is saving you from.
  mods.push({
    source: SRC_TERRAIN[terrainIdx]!, stat: "movement", op: "add",
    value: TERRAIN_INDEX_COST[terrainIdx]! - MOVE_COST_BASE,
    stackGroup: GROUP_SURFACE, stackRule: "first",
  });

  // resolveStat applies mulFp before min/max, so under a non-default diagonal
  // multiplier the floor and the native cap are pre-scaled here. That keeps
  // "minimum straight-step cost" a straight-step number in both readings.
  const scale = (v: number): number =>
    diagonal && rules.diagonalFp !== DIAGONAL_FP_EQUAL ? mulFp(v, rules.diagonalFp) : v;

  if (!onRoad && riverCost === null) {
    const off = offRoadTerrainCost(m, to, rules);
    if (off.overridden) {
      mods.push({ source: SRC_TERRAIN_COST, stat: "movement", op: "min", value: scale(off.cost) });
    } else if (rules.pathfinding > 0) {
      mods.push({
        source: SRC_PATHFINDING, stat: "movement", op: "add",
        value: -PATHFINDING_REDUCTION[rules.pathfinding]!,
      });
      // `max` raises a floor in resolveStat; this is the 95 / 90 / 85 clamp.
      mods.push({
        source: SRC_PATHFINDING_FLOOR, stat: "movement", op: "max",
        value: scale(PATHFINDING_FLOOR[rules.pathfinding]!),
      });
    }
    const [, , z] = cellCoords(m, to);
    if (!off.overridden && isNativeTerrain(rules, terrainIdx, z)) {
      mods.push({ source: SRC_NATIVE, stat: "movement", op: "min", value: scale(MOVE_COST_BASE) });
    }
  }

  if (diagonal && rules.diagonalFp !== DIAGONAL_FP_EQUAL) {
    mods.push({
      source: SRC_DIAGONAL, stat: "movement", op: "mulFp", value: rules.diagonalFp,
    });
  }

  return mods;
}

/**
 * Full, tooltip-ready breakdown of a single step. `value` is 0 when the step is
 * not legal for these rules - an illegal step is priced at nothing rather than
 * reported as an error, because the pathfinder simply does not relax it.
 */
export function explainStepCost(
  m: AdventureMapState, from: number, to: number, rules: MoveRules,
): Explained<number> {
  if (!cellEnterable(m, to, rules)) return { value: IMPASSABLE, base: MOVE_COST_BASE, terms: [] };
  const [fx, fy] = cellCoords(m, from);
  const [tx, ty] = cellCoords(m, to);
  const diagonal = fx !== tx && fy !== ty;
  return resolveStat(MOVE_COST_BASE, stepModifiers(m, from, to, rules, diagonal));
}

/** The hot path: the same number `explainStepCost` produces, without the terms. */
export function stepCost(
  m: AdventureMapState, from: number, to: number, rules: MoveRules, diagonal: boolean,
): number {
  const roadFrom = m.road[from]!, roadTo = m.road[to]!;
  let cost: number;
  if (roadFrom !== 0 && roadTo !== 0) {
    cost = roadPairCost(roadFrom, roadTo, rules);
  } else {
    const river = riverPairCost(m, from, to, rules);
    cost = river !== null ? river : offRoadTerrainCost(m, to, rules).cost;
  }
  if (diagonal && rules.diagonalFp !== DIAGONAL_FP_EQUAL) cost = mulFp(cost, rules.diagonalFp);
  return cost;
}
